import { noCase } from 'change-case';
import { useEffect, useState, useMemo, useRef } from 'react';
// @mui
import { alpha } from '@mui/material/styles';
import {
  Box,
  List,
  Badge,
  Button,
  Avatar,
  Tooltip,
  Divider,
  IconButton,
  Typography,
  ListItemText,
  ListSubheader,
  ListItemAvatar,
  ListItemButton,
  MenuItem,
  Chip,
  Popper,
  Paper,
  Fade,
  TextField,
  InputAdornment,
  Stack,
} from '@mui/material';
// utils
import { fToNow } from '../../../utils/formatTime';
import createAvatar from '../../../utils/createAvatar';
// hooks
import useLocales from '../../../hooks/useLocales';
import useAuth from '../../../hooks/useAuth';
import useTenantMode from '../../../hooks/useTenantMode';
// redux
import { useDispatch, useSelector } from '../../../redux/store';
import { getVenues, setTenantVenue } from '../../../redux/slices/venue';
// _mock_
import { _notifications } from '../../../_mock';
// components
import Iconify from '../../../components/Iconify';
import AuthenticatedAvatar from '../../../components/AuthenticatedAvatar';
import Scrollbar from '../../../components/Scrollbar';
import MenuPopover from '../../../components/MenuPopover';
import { IconButtonAnimate } from '../../../components/animate';
import BadgeStatus from '../../../components/BadgeStatus';
import VenueStatusBadge from '../../../components/VenueStatusBadge';
import VenueLimitAlert from '../../../components/VenueLimitAlert';
import Label from '../../../components/Label';
// types
import { VenueManager } from '../../../@types/venue';
// translations
import { tokens } from '../../../locales/tokens';

// ----------------------------------------------------------------------

const ITEM_HEIGHT = 64;
const ONBOARDING_STORAGE_KEY = 'check_tenant_onboarding_seen';
const RECENT_VENUES_STORAGE_KEY = 'check_recent_venues';
const MAX_RECENT_VENUES = 4;

function readRecentVenueIds(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_VENUES_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

// ----------------------------------------------------------------------

export default function VenuePopover() {
  const [venue, setVenue] = useState(_notifications);

  const { t } = useLocales();
  const { user } = useAuth();

  const totalUnRead = venue.filter((item) => item.isUnRead === true).length;

  const { venues, tenantVenue } = useSelector((state) => state.venue);
  const { operationalSummary } = useSelector((state) => state.order);

  const { isOrganizationMode, color: tenantModeColor } = useTenantMode();

  const [open, setOpen] = useState<HTMLElement | null>(null);

  const anchorRef = useRef<HTMLButtonElement>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [recentVenueIds, setRecentVenueIds] = useState<string[]>(readRecentVenueIds);

  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(getVenues());
  }, [dispatch]);

  // Initialize tenantVenue when user/venues load. Users with a single venue
  // skip the organization concept entirely — there's nothing to switch
  // between, so we default straight into that venue instead of "all".
  useEffect(() => {
    if (!user?.organization_id) return;

    if (venues.length === 1) {
      const onlyVenue = venues[0];
      const alreadySelected = typeof tenantVenue === 'object' && tenantVenue?.id === onlyVenue.id;
      if (!alreadySelected) {
        dispatch(setTenantVenue(onlyVenue));
      }
      return;
    }

    if (tenantVenue === 'all' || !tenantVenue) {
      dispatch(setTenantVenue(user.organization_id));
    }
  }, [user?.organization_id, tenantVenue, venues, dispatch]);

  // First-session coachmark for multi-venue users — explains the selector
  // once, then never again (tracked in localStorage, not per-device state
  // we'd need a backend field for).
  useEffect(() => {
    if (venues.length <= 1) return;
    if (localStorage.getItem(ONBOARDING_STORAGE_KEY)) return;
    setShowOnboarding(true);
  }, [venues.length]);

  const dismissOnboarding = () => {
    localStorage.setItem(ONBOARDING_STORAGE_KEY, '1');
    setShowOnboarding(false);
  };

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setOpen(event.currentTarget);
    if (showOnboarding) dismissOnboarding();
  };

  const handleClose = () => {
    setOpen(null);
  };

  const handleSetTenant = (venue: VenueManager | string) => {
    // If 'all' is passed, use organization_id instead
    if (venue === 'all' && user?.organization_id) {
      dispatch(setTenantVenue(user.organization_id))
    } else {
      dispatch(setTenantVenue(venue))
      if (typeof venue === 'object' && venue?.id) {
        const nextRecent = [venue.id, ...recentVenueIds.filter((id) => id !== venue.id)].slice(
          0,
          MAX_RECENT_VENUES
        );
        setRecentVenueIds(nextRecent);
        localStorage.setItem(RECENT_VENUES_STORAGE_KEY, JSON.stringify(nextRecent));
      }
    }
    handleClose()
  }

  // Small "N pedidos abertos" / "Fechado" line per venue — reuses the
  // operational summary already fetched for the home dashboard's KPIs
  // (refreshed on every tenant switch), no extra request needed here.
  const getVenueStatusText = (targetVenue: VenueManager) => {
    if (!targetVenue.openstatus) return t(tokens.tenantContext.closed);
    const summary = operationalSummary?.venues.find((v) => v.venue_id === targetVenue.id);
    return summary ? t(tokens.tenantContext.openOrdersCount, { count: summary.active_orders }) : undefined;
  };

  const searchedVenues = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return venues;
    return venues.filter((venue) => venue.name.toLowerCase().includes(query));
  }, [venues, searchQuery]);

  const recentVenues = useMemo(() => {
    if (searchQuery) return [];
    return recentVenueIds
      .map((id) => venues.find((venue) => venue.id === id))
      .filter((venue): venue is VenueManager => Boolean(venue));
  }, [recentVenueIds, venues, searchQuery]);

  // Group venues by organization
  const venuesByOrganization = useMemo(() => {
    const grouped: Record<string, { organization_name: string; venues: VenueManager[] }> = {};

    searchedVenues.forEach((venue) => {
      const orgId = venue.organization_id || 'no-organization';
      const orgName = venue.organization_name || 'No Organization';

      if (!grouped[orgId]) {
        grouped[orgId] = {
          organization_name: orgName,
          venues: [],
        };
      }

      grouped[orgId].venues.push(venue);
    });

    return grouped;
  }, [searchedVenues]);

  const totalVenues = venues.length;

  // A single-venue account has nothing to switch between — showing a
  // clickable selector there is a control that never leads anywhere.
  if (totalVenues === 1) {
    return (
      <Box sx={{ pl: 3, pr: 3, display: 'flex', alignItems: 'center' }}>
        <Typography color="text.primary" variant="h4">
          {venues[0].name}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Button
        ref={anchorRef}
        onClick={handleOpen}
        color='inherit'
        sx={{
          pl: 3,
          pr: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          ...(showOnboarding && {
            borderRadius: 1,
            boxShadow: (theme) =>
              `0 0 0 3px ${theme.palette.primary.main}, 0 0 0 6px ${alpha(theme.palette.primary.main, 0.24)}`,
          }),
        }}
      >
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            bgcolor: tenantModeColor,
            flexShrink: 0,
          }}
        />
        <Typography
          color="text.primary"
          variant="h4"
        >
          {typeof tenantVenue === 'string' && tenantVenue !== 'all'
            ? (user?.organization_name || t(tokens.popover.tenant.generalTitle))
            : typeof tenantVenue === 'object' && tenantVenue.name}
        </Typography>
      </Button>

      <Popper
        open={showOnboarding}
        anchorEl={anchorRef.current}
        placement="bottom"
        transition
        sx={{ zIndex: (theme) => theme.zIndex.modal }}
        modifiers={[{ name: 'offset', options: { offset: [0, 12] } }]}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={200}>
            <Paper
              sx={{
                width: 280,
                p: 2,
                boxShadow: (theme) => theme.customShadows.z20,
              }}
            >
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                {t(tokens.tenantContext.onboardingMessage, { count: totalVenues })}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button size="small" variant="contained" onClick={dismissOnboarding}>
                  {t(tokens.tenantContext.onboardingCta)}
                </Button>
              </Box>
            </Paper>
          </Fade>
        )}
      </Popper>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={handleClose}
        sx={{ width: 360, p: 0, mt: 1.5, ml: 0.75, alignSelf: 'center' }}
        arrow='top-center'
      >
        <Box sx={{ display: 'flex', alignItems: 'center', py: 2, px: 2.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1">
            {t(tokens.popover.tenant.header)}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t(tokens.popover.tenant.subtitle)}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <VenueLimitAlert compact asLink onNavigate={handleClose} sx={{ mx: 2, mt: 1.5 }} />

        {!searchQuery && (
          <Box sx={{ px: 2, pt: 1.5 }}>
            <MenuItem
              onClick={() => handleSetTenant(user?.organization_id || 'all')}
              disabled={!user?.organization_id}
            sx={{
              borderRadius: 1,
              py: 1,
              bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity / 2),
              '&:hover': {
                bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
              },
            }}
          >
            <ListItemAvatar>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16),
                  color: 'primary.main',
                }}
              >
                <Iconify icon="mdi:office-building-outline" width={20} height={20} />
              </Box>
            </ListItemAvatar>
            <ListItemText
              primaryTypographyProps={{ typography: 'subtitle2', color: 'text.primary' }}
              secondaryTypographyProps={{ typography: 'caption', color: 'primary.main' }}
              primary={t(tokens.popover.tenant.viewOrganization)}
              secondary={`${user?.organization_name || ''}${user?.organization_name ? ' · ' : ''}${totalVenues} ${t(tokens.nav.venues).toLowerCase()}`}
            />
              {isOrganizationMode && (
                <Label variant={'ghost'} color={'primary'} sx={{ height: 20, fontSize: '0.65rem', ml: 1 }}>
                  {t(tokens.common.selected)}
                </Label>
              )}
            </MenuItem>
          </Box>
        )}

        <Box sx={{ px: 2, pt: 1.5, pb: 0.5, mb: 1.5 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={t(tokens.tenantContext.searchPlaceholder)}
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled', width: 18, height: 18 }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {recentVenues.length > 0 && (
          <Box sx={{ px: 2, pb: 1 }}>
            <Typography variant="overline" sx={{ color: 'text.secondary' }}>
              {t(tokens.tenantContext.recent)}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.75, flexWrap: 'wrap', gap: 1 }}>
              {recentVenues.map((venue) => (
                <Chip
                  key={venue.id}
                  label={venue.name}
                  size="small"
                  onClick={() => handleSetTenant(venue)}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Stack>
          </Box>
        )}

        <Typography variant="h6" sx={{ p: 1.5, pt: 0.5 }}>
          {t(tokens.nav.venues)} <Typography component="span">({searchedVenues.length})</Typography>
        </Typography>

        {searchQuery && searchedVenues.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', px: 2.5, pb: 3 }}>
            {t(tokens.tenantContext.noResults)}
          </Typography>
        ) : (
          <Scrollbar sx={{ height: ITEM_HEIGHT * 6 }}>
            {Object.entries(venuesByOrganization).map(([orgId, { organization_name, venues: orgVenues }]) => (
              <Box key={orgId}>
                {orgVenues.map((venue) => {
                  const isSelected = typeof tenantVenue === 'object' && tenantVenue.id === venue.id;
                  const statusText = getVenueStatusText(venue);
                  return (
                    <MenuItem key={venue.id} onClick={() => handleSetTenant(venue)}>
                      <ListItemAvatar sx={{ position: 'relative' }}>
                        <AuthenticatedAvatar
                          alt={venue.name}
                          color={createAvatar(venue.name).color}
                          src={venue.photo_url}
                          sx={{ borderRadius: 6, width: 48, height: 48 }}
                        >
                          {createAvatar(venue.name).name}
                        </AuthenticatedAvatar>
                        <VenueStatusBadge
                          status={venue.openstatus ? 'open' : 'closed'}
                          sx={{ position: 'absolute', right: 1, top: 1 }}
                        />
                      </ListItemAvatar>

                      <ListItemText
                        primaryTypographyProps={{ typography: 'subtitle2', mb: 0.25 }}
                        secondaryTypographyProps={{ typography: 'caption' }}
                        primary={venue.name}
                        secondary={`${venue.street}, ${venue.number}`}
                      />

                      {/* {isSelected && (
                        <Label
                          variant={'ghost'}
                          color={'primary'}
                          sx={{ height: 20, fontSize: '0.65rem', ml: 1, flexShrink: 0 }}
                        >
                          {t(tokens.common.selected)}
                        </Label>
                      )} */}

                      {isSelected ? (
                        <Label
                          variant={'ghost'}
                          color={'primary'}
                          sx={{ height: 20, fontSize: '0.65rem', ml: 1, flexShrink: 0 }}
                        >
                          {t(tokens.common.selected)}
                        </Label>
                      ) : (
                        statusText && (
                          <Typography
                            variant="caption"
                            sx={{
                              ml: 1,
                              flexShrink: 0,
                              whiteSpace: 'nowrap',
                              color: venue.openstatus ? 'success.main' : 'text.disabled',
                            }}
                          >
                            {statusText}
                          </Typography>
                        )
                      )}
                    </MenuItem>
                  );
                })}
                {Object.keys(venuesByOrganization).indexOf(orgId) < Object.keys(venuesByOrganization).length - 1 && (
                  <Divider sx={{ my: 0.5 }} />
                )}
              </Box>
            ))}
          </Scrollbar>
        )}
      </MenuPopover>
    </>
  );
}